import React, { useState, useEffect } from "react";
import ImageUploadField from "../../components/ImageUploadField";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

// Import tab components
import SettingsTabNav from "./settings/SettingsTabNav";
import SettingsMessage from "./settings/SettingsMessage";
import FeesAndRevenue from "./settings/FeesAndRevenue";
import SubscriptionSettings from "./settings/SubscriptionSettings";
import SMSMarketingSettings from "./settings/SMSMarketingSettings";
import BusinessRulesSettings from "./settings/BusinessRulesSettings";
import BoothRentalSettings from "./settings/BoothRentalSettings";
import SecuritySettings from "./settings/SecuritySettings";
import LooksSettings from "./settings/LooksSettings";
import DemoModeSettings from "./settings/DemoModeSettings";
import HomepageSettings from "./settings/HomepageSettings";
import StorefrontGatingSettings from "./settings/StorefrontGatingSettings";

// Import custom hook
import { useSettingsState } from "./settings/useSettingsState";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const PlatformSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get active tab from URL or default to 'fees'
  const activeTab = searchParams.get("tab") || "fees";
  const apiBaseUrl = (API_BASE_URL || "").toLowerCase();
  const isDemoEnvironment =
    !apiBaseUrl ||
    apiBaseUrl.includes("api-demo") ||
    apiBaseUrl.includes("localhost") ||
    apiBaseUrl.includes("127.0.0.1");

  // Use the custom hook for state management
  const {
    settings,
    loading,
    saving,
    message,
    hasUnsavedChanges,
    fetchSettings,
    saveSettings,
    updateSettings,
    resetSettings,
    setMessage,
  } = useSettingsState();

  useEffect(() => {
    checkPortalAdmin();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isDemoEnvironment && activeTab === "demo") {
      setSearchParams({ tab: "fees" });
    }
  }, [activeTab, isDemoEnvironment, setSearchParams]);

  const checkPortalAdmin = () => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin"); // Redirect to portal admin dashboard, not business dashboard
        return;
      }
    } catch (error) {
      navigate("/login");
    }
  };

  const handleTabChange = (newTab) => {
    setSearchParams({ tab: newTab });
  };

  const handleClearMessage = () => {
    setMessage({ type: "", text: "" });
  };

  // ---- Look watermark logo -------------------------------------------------
  const watermarkLogoUrl = settings?.lookWatermark?.logoUrl || "";
  const [watermarkBusy, setWatermarkBusy] = useState(false);

  /**
   * The upload field hands back the new value: a URL after a successful upload, or
   * "" when its remove control (or the button below) clears it. Uploads already
   * persist server-side, but a removal has to be sent explicitly - the previous
   * handler accepted no value at all and just refetched, which is why the logo
   * could never be deleted.
   */
  const handleWatermarkChange = async (nextValue) => {
    const url = typeof nextValue === "string" ? nextValue : "";

    if (url) {
      setMessage({
        type: "success",
        text: "Watermark logo uploaded successfully",
      });
      await fetchSettings();
      return;
    }

    setWatermarkBusy(true);
    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.delete(
        `${API_BASE_URL}/api/admin/settings/look-watermark-logo`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMessage({ type: "success", text: "Watermark logo removed" });
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Failed to remove the watermark logo",
      });
    } finally {
      setWatermarkBusy(false);
    }

    await fetchSettings();
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading settings...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "40px 20px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ marginBottom: "32px" }}>
          <button
            onClick={() => navigate("/portal-admin")}
            style={{
              background: "none",
              border: "none",
              color: "#6b7280",
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 0",
              marginBottom: "16px",
            }}
          >
            ← Back to Portal Admin
          </button>
          <h1
            style={{ fontSize: "32px", fontWeight: "700", margin: "0 0 8px" }}
          >
            ⚙️ Platform Settings
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Configure platform-wide settings across all modules
          </p>
        </div>

        <SettingsMessage message={message} onClear={handleClearMessage} />

        <div
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginBottom: "20px",
          }}
        >
          <h3 style={{ margin: "0 0 8px", fontSize: "18px" }}>
            🖼️ Look Watermark Logo
          </h3>
          <p style={{ margin: "0 0 14px", color: "#6b7280", fontSize: "14px" }}>
            Upload the logo used for look image watermarking. This is stored in
            platform settings and loaded by backend watermark processing.
          </p>

          {watermarkLogoUrl ? (
            <div style={{ marginBottom: "12px" }}>
              <img
                src={watermarkLogoUrl}
                alt="Current watermark logo"
                style={{
                  maxHeight: "80px",
                  maxWidth: "240px",
                  objectFit: "contain",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "6px",
                  background: "#fafafa",
                }}
              />
            </div>
          ) : null}

          {/* One shared field: input + validation + HEIC + upload. Previews are off
              because this card renders its own preview above, and removal is an
              explicit button so it can be persisted server-side. */}
          <ImageUploadField
            variant="inline"
            buttonLabel={
              watermarkLogoUrl
                ? "Replace Watermark Logo"
                : "Upload Watermark Logo"
            }
            showPreviews={false}
            value={watermarkLogoUrl}
            onChange={handleWatermarkChange}
            endpoint={`${API_BASE_URL}/api/admin/settings/look-watermark-logo`}
            fieldName="logo"
            hint="PNG with a transparent background works best."
            onError={(message) => setMessage({ type: "error", text: message })}
          />

          {watermarkLogoUrl ? (
            <button
              type="button"
              onClick={() => handleWatermarkChange("")}
              disabled={watermarkBusy}
              style={{
                marginTop: "10px",
                padding: "8px 14px",
                border: "1px solid #ef4444",
                background: "#ffffff",
                color: "#ef4444",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 500,
                cursor: watermarkBusy ? "wait" : "pointer",
              }}
            >
              {watermarkBusy ? "Removing…" : "Remove logo"}
            </button>
          ) : null}
        </div>

        <SettingsTabNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          hasUnsavedChanges={hasUnsavedChanges}
          showDemoTab={isDemoEnvironment}
        />

        <div style={{ marginTop: "24px" }}>
          {activeTab === "fees" && (
            <FeesAndRevenue
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "subscriptions" && (
            <SubscriptionSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
              setMessage={setMessage}
            />
          )}

          {activeTab === "homepage" && (
            <HomepageSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
              setMessage={setMessage}
            />
          )}

          {activeTab === "storefront-gating" && (
            <StorefrontGatingSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "sms" && (
            <SMSMarketingSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "business" && (
            <BusinessRulesSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "booth-rental" && (
            <BoothRentalSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "security" && (
            <SecuritySettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {activeTab === "looks" && (
            <LooksSettings
              settings={settings}
              updateSettings={updateSettings}
              saveSettings={saveSettings}
              saving={saving}
              hasUnsavedChanges={hasUnsavedChanges}
              resetSettings={resetSettings}
            />
          )}

          {isDemoEnvironment && activeTab === "demo" && <DemoModeSettings />}
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default PlatformSettings;
